import {
  CONNECT_APP_BASE_URL,
  createConnectPopup,
  MONOLITH_BASE_URL,
} from "@zapier/utils";

export const OAUTH_ACCOUNT_ID_PARAM = "_zapier_account_id";
export const OAUTH_SHARED_PARAM = "_zapier_is_shared";
export const OAUTH_IS_MANAGED_PARAM = "_zapier_is_managed";
export const AUTH_ID_PARAM = "_zapier_auth_id";
export const AUTH_DESTINATION_SELECTED_API_PARAM =
  "_zapier_destination_selected_api";
export const AUTH_EXPECTED_IDENTIFIER_PARAM = "_zapier_expected_identifier";
const AUTH_FOR_SIGNUP_PARAM = "for_signup";

// Shared param for non-OAuth auths
export const SHARED_AUTH_PARAM = "isShared";

// Event type that should be emitted (postMessage) from the auth window
const AUTH_SUCCESS_EVENT_TYPE = "authenticationSuccess";

interface AuthSuccessMessage {
  type: typeof AUTH_SUCCESS_EVENT_TYPE;
  authId?: string;
}

const isAuthSuccessMessage = (data: unknown): data is AuthSuccessMessage =>
  typeof data === "object" &&
  data !== null &&
  (data as { type?: unknown }).type === AUTH_SUCCESS_EVENT_TYPE;

// Auth specific errors
export const AUTH_FAILED_CONN = "AUTH_FAILED_CONN";
export const AUTH_CONNECTION_ERROR = "AUTH_CONNECTION_ERROR";

// ConnectApp (connect.zapier.com) posts this to the opener on a failed
// connection — including a domain_filter guard rejection, whose message is
// carried in error_description. See ER-530.
const CONNECTION_ERROR_EVENT_TYPE = "connectionError";

interface ConnectionErrorMessage {
  type: typeof CONNECTION_ERROR_EVENT_TYPE;
  error?: string;
  error_description?: string;
  service?: string;
}

export const isConnectionErrorMessage = (
  data: unknown,
): data is ConnectionErrorMessage =>
  typeof data === "object" &&
  data !== null &&
  (data as { type?: unknown }).type === CONNECTION_ERROR_EVENT_TYPE;

export interface ConnectionErrorPayload {
  error?: string;
  errorDescription?: string;
}

type ConnectionRejectionError = Error & {
  code: string;
  connectionError: ConnectionErrorPayload;
};

const newConnectionError = (
  payload: ConnectionErrorPayload,
): ConnectionRejectionError => {
  const error = new Error(
    payload.errorDescription || "Failed to connect account.",
  ) as ConnectionRejectionError;
  error.code = AUTH_CONNECTION_ERROR;
  error.connectionError = payload;
  return error;
};

const newAuthError = (code: string, message: string) => {
  const error = new Error(message);
  (error as any).code = code;
  return error as Error;
};

export const addParametersToUrl = (
  url: string,
  parameters: Record<string, any>,
) => {
  const isRelativeUrl = url.startsWith("/");
  const baseUrl =
    isRelativeUrl && globalThis.window !== undefined
      ? globalThis.location.origin
      : MONOLITH_BASE_URL;
  const urlObject = new URL(url, baseUrl);
  Object.keys(parameters).forEach((key) => {
    //only append the params with truthy values, or "false", null and undefined will not be appended, and don't append duplicate params
    if (
      parameters[key] !== undefined &&
      parameters[key] !== null &&
      !urlObject.searchParams.has(key)
    ) {
      urlObject.searchParams.append(key, parameters[key]);
    }
  });
  return urlObject.toString();
};

interface ConnectUrlParameters {
  serviceKey?: string;
  accountId?: number;
  isShared?: boolean;
  isManaged?: boolean;
  authId?: string;
  authIdentifier?: string;
  destinationServiceKey?: string;
  isForSignup?: boolean;
}

export const getConnectUrl = ({
  serviceKey,
  accountId,
  isShared,
  isManaged,
  authId,
  authIdentifier,
  destinationServiceKey,
  isForSignup,
}: ConnectUrlParameters) => {
  return addParametersToUrl(`/engine/auth/start/${serviceKey}/`, {
    [OAUTH_ACCOUNT_ID_PARAM]: accountId,
    [OAUTH_SHARED_PARAM]: isShared,
    [OAUTH_IS_MANAGED_PARAM]: isManaged,
    [AUTH_ID_PARAM]: authId,
    [AUTH_EXPECTED_IDENTIFIER_PARAM]: authIdentifier,
    [AUTH_DESTINATION_SELECTED_API_PARAM]: destinationServiceKey,
    [AUTH_FOR_SIGNUP_PARAM]: isForSignup,
  });
};

// Message creator to post from authentication window on successful connection.
export const createAuthConnectionMessage = (authId?: string) => ({
  type: AUTH_SUCCESS_EVENT_TYPE,
  authId,
});

// Takes `win` popup window that authorizes a connection.
// Returns a `Promise` that will `resolve` if an
// `authId` is provided by `win` or `reject` if no
// `authId` is provided. `win` provides `authId` via
// a `postMessage` event to our `window`. We `poll`
// until `win` is `closed`. Would be nice to make that
// event based using `unload` or something, but not
// having a lot of luck with that.
//
// The popup may complete on the monolith origin OR on the ConnectApp
// origin (connect.zapier.com), so both are accepted. Other postMessages
// on the page (RUM, extensions) are ignored via `isAuthSuccessMessage`,
// and `event.source` is matched to the popup so concurrent connection
// attempts cannot resolve each other's promises.
const waitForConnection = (win: Window, isOauth?: boolean) => {
  return new Promise((resolve, reject) => {
    const allowedOrigins = new Set([MONOLITH_BASE_URL, CONNECT_APP_BASE_URL]);
    if (globalThis.window !== undefined) {
      allowedOrigins.add(globalThis.location.origin);
    }
    let authId: string | undefined;
    let success = false;
    let connectionError: ConnectionErrorMessage | undefined;

    const onMessage = (event: MessageEvent) => {
      if (event.source !== win || !allowedOrigins.has(event.origin)) {
        return;
      }
      if (isAuthSuccessMessage(event.data)) {
        authId = event.data.authId;
        success = true;
      } else if (isConnectionErrorMessage(event.data)) {
        connectionError = event.data;
      }
    };
    window.addEventListener("message", onMessage);

    const poll = () => {
      if (win.closed) {
        // If the parent window loses focus, then we'll detect the window is
        // closed before we receive our message. Wait for the next event turn
        // to make sure all messages are flushed.
        setTimeout(() => {
          window.removeEventListener("message", onMessage);

          // oauth will not always return auth id
          if (!success || (authId == null && !isOauth)) {
            if (connectionError) {
              reject(
                newConnectionError({
                  error: connectionError.error,
                  errorDescription: connectionError.error_description,
                }),
              );
            } else {
              reject(
                newAuthError(AUTH_FAILED_CONN, "Failed to connect account."),
              );
            }
            return;
          }

          resolve(authId);
        }, 0);
      } else {
        setTimeout(poll, 1000);
      }
    };
    poll();
  });
};

export const safelyAddSharedAuthParametersToUrl = (
  url: string,
  parameters: ConnectUrlParameters,
  isOauth?: boolean,
  hasUpfrontFields?: boolean,
) => {
  // It's only safe to add params to the URL if the auth URL is internal.
  // Tacking on params to an external url like an OAuth authorize URL might get
  // rejected by the partner app.
  // We still need to add the params if there are upfront fields, or the auth
  // won't initialize properly.
  const isSafeToAddParametersToUrl = !isOauth || hasUpfrontFields;
  return isSafeToAddParametersToUrl
    ? addParametersToUrl(url, {
        [SHARED_AUTH_PARAM]: parameters.isShared,
        [OAUTH_IS_MANAGED_PARAM]: parameters.isManaged,
        [AUTH_ID_PARAM]: parameters.authId,
      })
    : url;
};

/**
 * This function will open a new connect auth window. If it is possible, this
 * function will first open a splash (loading) screen and then redirect to the
 * desired auth configuration, or it will immediately open the auth config.
 */

interface ConnectToAuthParameters {
  accountId: number | undefined;
  authId?: string;
  authIdentifier?: string;
  destinationServiceKey?: string;
  hasUpfrontFields?: boolean;
  isForSignup?: boolean;
  isOauth: boolean;
  isShared?: boolean;
  isManaged?: boolean;
  onError?: (error?: any) => void;
  onTrack?: (event: string, options: Record<string, unknown>) => Promise<any>;
  serviceKey?: string;
}

export const connectToAuth = async ({
  accountId,
  hasUpfrontFields,
  isShared,
  isManaged,
  isOauth,
  serviceKey,
  authId,
  authIdentifier, // Require created auth to have this identity.
  destinationServiceKey,
  onTrack = () => Promise.resolve(),
  isForSignup,
}: ConnectToAuthParameters) => {
  const parameters = {
    accountId,
    isShared,
    isManaged,
    serviceKey,
    authId,
    authIdentifier,
    destinationServiceKey,
    isForSignup,
  };

  // Create the splash popup if possible, then retrieve the connect URL
  // to ensure it is valid. We do these as separate steps because we want the
  // popup window to open ASAP rather than waiting for the `fetchConnectUrl` to
  // resolve.

  const url = await getConnectUrl(parameters);
  const parameterizedUrl = safelyAddSharedAuthParametersToUrl(
    url,
    parameters,
    isOauth,
    hasUpfrontFields,
  );

  const win = createConnectPopup(parameterizedUrl);

  onTrack("auth: redirect to first leg", {
    accountId,
    authId,
    isForSignup,
    isOauth,
    parameterizedUrl,
    serviceKey,
  });

  try {
    const conn = await waitForConnection(win, isOauth);

    onTrack("auth: connection success", {
      accountId,
      authId,
      conn,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    return conn;
  } catch (error: any) {
    onTrack("auth: could not get connection", {
      accountId,
      authId,
      error: error.message,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    throw error;
  }
};
